import React from 'react';
import { Chip, Stack, Tooltip, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';

interface SuggestionsProps {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  onSuggestionEdit?: (suggestion: string) => void;
  disabled?: boolean;
  isFullscreen?: boolean;
}

const SuggestionsContainer = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== 'isFullscreen',
})<{ isFullscreen?: boolean }>(({ theme, isFullscreen }) => ({
  padding: '8px 16px',
  display: 'flex',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '-ms-overflow-style': 'none',
  'scrollbar-width': 'none',
}));

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isFullscreen',
})<{ isFullscreen?: boolean }>(({ theme, isFullscreen }) => ({
  margin: '4px',
  backgroundColor: 'transparent',
  paddingRight: '4px',
  height: isFullscreen ? '32px' : '24px',
  '& .MuiChip-label': {
    maxWidth: isFullscreen ? '30ch' : '25ch',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: isFullscreen ? '0 12px' : '0 8px',
    fontSize: isFullscreen ? '0.875rem' : '0.75rem',
  },
  transition: 'all 0.2s ease',
}));

const EditButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isFullscreen',
})<{ isFullscreen?: boolean }>(({ theme, isFullscreen }) => ({
  padding: isFullscreen ? 2 : 1,
  marginLeft: -4,
  marginRight: 4,
  backgroundColor: 'transparent',
  width: isFullscreen ? '20px' : '16px',
  height: isFullscreen ? '20px' : '16px',
  minWidth: 'auto',
  '& .MuiSvgIcon-root': {
    fontSize: isFullscreen ? '0.9rem' : '0.75rem',
    color: theme.palette.text.secondary,
    transition: 'all 0.2s ease',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    '& .MuiSvgIcon-root': {
      color: '#fff',
    },
  },
}));

const SuggestionWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isFullscreen',
})<{ isFullscreen?: boolean }>(({ theme, isFullscreen }) => ({
  display: 'flex', 
  alignItems: 'center',
  backgroundColor: '#f5f7fb',
  borderRadius: isFullscreen ? '16px' : '12px',
  height: isFullscreen ? '32px' : '24px',
  '&:hover': {
    backgroundColor: theme.customProps.primaryColor,
    color: '#fff',
    '& .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
    '& .MuiChip-root': {
      color: '#fff',
    },
    [`& .${EditButton}`]: {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        '& .MuiSvgIcon-root': {
          color: '#fff',
        },
      },
    },
  },
}));

export const Suggestions: React.FC<SuggestionsProps> = ({
  suggestions,
  onSuggestionClick,
  onSuggestionEdit,
  disabled = false,
  isFullscreen = false,
}) => {
  const handleEditClick = (e: React.MouseEvent, suggestion: string) => {
    e.stopPropagation();
    if (onSuggestionEdit) {
      onSuggestionEdit(suggestion);
    }
  };

  return (
    <SuggestionsContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      sx={{ justifyContent: isFullscreen ? 'center' : 'flex-start' }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'nowrap', minWidth: 'min-content' }}
      >
        {suggestions.map((suggestion, index) => (
          <Tooltip 
            key={index} 
            title={suggestion} 
            arrow
            PopperProps={{
              disablePortal: false,
              sx: { zIndex: 99999999 }
            }}
          >
            <SuggestionWrapper isFullscreen={isFullscreen}>
              <StyledChip
                label={suggestion}
                onClick={() => onSuggestionClick(suggestion)}
                disabled={disabled}
                clickable
                isFullscreen={isFullscreen}
              />
              {onSuggestionEdit && (
                <EditButton
                  onClick={(e) => handleEditClick(e, suggestion)}
                  disabled={disabled}
                  isFullscreen={isFullscreen}
                >
                  <EditIcon />
                </EditButton>
              )}
            </SuggestionWrapper>
          </Tooltip>
        ))}
      </Stack>
    </SuggestionsContainer>
  );
};